import type { CSSProperties } from 'react';

/**
 * Shared layout for the "info + steps" two-card pages — the Workflow Details
 * screen (originator) and the Approver Review screen mirror the same shell.
 * Keeping the inline style constants in one place stops them from drifting
 * apart whenever one page gets a polish pass.
 */

export const OUTER_WRAP: CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  padding: 20,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

export const COLS: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '360px 1fr',
  gap: 16,
  alignItems: 'stretch',
  minHeight: 480,
};

export const CARD: CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

export const CARD_HEADER: CSSProperties = {
  background: '#f6f7f8',
  borderBottom: '1px solid #e5e7eb',
  padding: '12px 20px',
  minHeight: 81,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const CARD_HEADER_TITLE: CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: '#111827',
};

export const CARD_BODY: CSSProperties = {
  padding: 20,
  flex: 1,
};

export const FIELD_BLOCK: CSSProperties = {
  marginBottom: 16,
};

export const FIELD_LABEL: CSSProperties = {
  display: 'block',
  // Matches INLINE_ROW so block-layout rows (Name, Description) read at the same
  // visual weight as the inline rows below them (Created by, Assigned to, etc.).
  fontSize: 14,
  fontWeight: 700,
  color: '#111827',
  marginBottom: 4,
};

export const FIELD_VALUE: CSSProperties = {
  display: 'block',
  fontSize: 14,
  color: '#4b5563',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
};

export const INLINE_ROW: CSSProperties = {
  fontSize: 14,
  color: '#4b5563',
  marginBottom: 10,
};

export const INLINE_LABEL: CSSProperties = {
  fontWeight: 700,
  color: '#111827',
  marginRight: 4,
};

/** Shows the fallback string when the value is null, undefined, or whitespace. */
export const emptyValue = (value: string | null | undefined, fallback: string): string =>
  value && value.trim().length > 0 ? value : fallback;
